import { useEffect, useRef, useCallback, useMemo } from "react";
import { Client, StompSubscription } from "@stomp/stompjs";
import { debounce } from "lodash";
import { useNotificationContext } from "providers";
import {
  NOTIFICATION_TYPE_ENUM,
  RequestStatusesEventData,
  TransactionEventData,
  UserWalletCheckEventData,
  RequestProcessingEventData,
  WebSocketState,
  PROCESSING_TYPE,
  RequestWalletCheckEventData,
  AmlStatusEventData,
  NominalAccountDepositEventData,
  QuoteExtendedEventData,
  DoubleControlEventData,
  OPERATION_TYPE,
  BalanceEventData,
  TOKEN_BALANCE_ENUM,
  BALANCE_ENUM,
  BankStatementUpdatedEventData,
  DocumentUpdatedEventData,
  NominalAccountUpdatedWebSocketEventData,
  BALANCE_TYPE_ENUM,
  PAGE_PATH_ENUM,
  RubOperationWebSocketEventData,
  RubOperationsFeeUpdatedEvent,
  rubWithdrawalRequestSafeDealSucceedEvent,
  safeDealStateTexts,
  ContractWebSocketEventData,
  QuoteRejectWebSocketEventData,
  ContractStatusUpdatedWebSocketEventData,
  CONTRACT_STATUSES,
} from "interfaces";
import { timeFormat } from "utils/date";
import { ENDPOINTS } from "services";
import { useStores } from "stores";
import { routes } from "constants/routes";
import { getGlobalFetchBalance } from "./useGlobalBalance";
import styles from "providers/NotificationProvider/notification.module.css";
import { DIRECT_PAYMENT_CUSTOMER_LP, TRANSACTION_REQUEST_DElAY } from "utils/envConstants";
import { formatWithDecimals } from "utils";
import { Flex, Typography } from "antd";

let globalWebSocketState: WebSocketState = {
  isConnected: false,
  client: null,
};

let walletCheckEvents: Map<string, UserWalletCheckEventData> = new Map();
const walletCheckSubscribers = new Set<(amlId: string, event: UserWalletCheckEventData) => void>();

let walletRiskEvents: Map<string, RequestWalletCheckEventData> = new Map();
const walletRiskSubscribers = new Set<(checkId: string, event: RequestWalletCheckEventData) => void>();

let amlCheckStatusEvents: Map<string, AmlStatusEventData> = new Map();
const amlCheckStatusSubscribers = new Set<(bidId: string, event: AmlStatusEventData) => void>();

let balanceEvents: Map<string, BalanceEventData> = new Map();
const balanceSubscribers = new Set<(type: BALANCE_ENUM, event: BalanceEventData) => void>();

let nominalAccountUpdatedEvents: Map<string, NominalAccountUpdatedWebSocketEventData> = new Map();
const nominalAccountUpdatedSubscribers = new Set<
  (type: BALANCE_TYPE_ENUM, event: NominalAccountUpdatedWebSocketEventData) => void
>();

const subscribers = new Set<() => void>();

const updateSubscribers = () => {
  subscribers.forEach((callback) => callback());
};

const notifyWalletCheckSubscribers = (amlId: string, event: UserWalletCheckEventData) => {
  walletCheckSubscribers.forEach((callback) => callback(amlId, event));
};

const notifyWalletRiskSubscribers = (checkId: string, event: RequestWalletCheckEventData) => {
  walletRiskSubscribers.forEach((callback) => callback(checkId, event));
};

const notifyAmlCheckStatusSubscribers = (bidId: string, event: AmlStatusEventData) => {
  amlCheckStatusSubscribers.forEach((callback) => callback(bidId, event));
};

const notifyBalanceSubscribers = (type: BALANCE_ENUM, event: BalanceEventData) => {
  balanceSubscribers.forEach((callback) => callback(event.balanceType, event));
};

const notifyNominalAccountUpdatedSubscribers = (
  type: BALANCE_TYPE_ENUM,
  event: NominalAccountUpdatedWebSocketEventData
) => {
  nominalAccountUpdatedSubscribers.forEach((callback) => callback(type, event));
};

export const useWebSocketNotifications = (userId?: string) => {
  const {
    bidCard,
    bidTransactions,
    bidWalletChecks,
    bids,
    contractCard,
    contractBids,
    user,
    rubWithdrawalRequests,
    rubOperations,
    nominalAccounts,
    quotes,
    pendingActionsBidList,
    bankStatements,
    pendingStatistics,
  } = useStores();
  const stompClientRef = useRef<Client | null>(null);
  const { openNotification } = useNotificationContext();
  const { isRiskManager, isSeniorOperator, isJuniorOperator, isCustomer, isOperator } = user.roles;

  const urlParams = new URLSearchParams(window.location.search);
  const isTransactionListOpen = urlParams.get("transactionList") === "open";

  const delayTimer = parseInt(TRANSACTION_REQUEST_DElAY) * 1000;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedBidsFetch = useMemo(
    () => debounce(() => bids.fetch({ isSilent: true }), 1000, { leading: true, trailing: true }),
    [bids]
  );

  useEffect(() => {
    return () => debouncedBidsFetch.cancel();
  }, [debouncedBidsFetch]);

  useEffect(() => {
    let stompClient: Client | null = null;

    if (userId) {
      stompClient = new Client({
        reconnectDelay: 5000,
        brokerURL: ENDPOINTS.webSocket(),
        onConnect: () => {
          globalWebSocketState.isConnected = true;
          globalWebSocketState.client = stompClient;
          updateSubscribers();

          // заявки
          stompClient!.subscribe(`/notifications/${userId}/request`, (message) => {
            const messageData = JSON.parse(message.body) as RequestStatusesEventData;

            const pagePath = window.location.pathname.replace("/", "");
            const isBidsGridPage = pagePath === routes.bids;

            const bidsId = pagePath.split("bids/")[1];
            const isBidInfoPage = bidsId && messageData.link.includes(bidsId);
            const isContractPage = pagePath.includes(`${routes.contracts}/`);

            if (isBidsGridPage) {
              debouncedBidsFetch();
            }

            if (isBidInfoPage) {
              bidCard.updateCurrentCard();
              if (isTransactionListOpen) {
                bidTransactions.fetch({ isSilent: true });
              }
            }

            if (isContractPage && contractBids.data.length > 0) {
              const isContractBid = contractBids.data.some((item) => messageData.link.includes(item.id));
              if (isContractBid) {
                contractCard.updateCardById(contractCard.card.id);
                contractBids.fetch({ isSilent: true });
              }
            }

            if (messageData.show) {
              const fetchBalance = getGlobalFetchBalance();
              if (fetchBalance) {
                fetchBalance();
              }

              openNotification({
                message: (
                  <div className={styles.notificationTitle}>
                    <span>Смена статуса заявки</span>
                    <span className={styles.notificationTitleTime}>{timeFormat(messageData.statusDate)}</span>
                    <span
                      className={styles.notificationBlockLink}
                      onClick={() => {
                        window.location.href = messageData.link;
                      }}
                    />
                  </div>
                ),
                description: `Заявка № ${messageData.requestNumber} перешла в статус «${messageData.status}»`,
                type: NOTIFICATION_TYPE_ENUM.NOTICE,
              });
            }
          });

          //проверки кошельков
          stompClient!.subscribe(`/notifications/${userId}/user-wallet-check`, (message) => {
            try {
              const eventData = JSON.parse(message.body) as UserWalletCheckEventData;

              walletCheckEvents.set(eventData.amlId, eventData);

              notifyWalletCheckSubscribers(eventData.amlId, eventData);

              const pagePath = window.location.pathname.replace("/", "");
              const bidsId = pagePath.split("bids/")[1];
              const isBidInfoPage = bidsId && eventData.link.includes(bidsId);

              if (isBidInfoPage) {
                bidWalletChecks.fetch({ isSilent: true });
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события проверки кошелька:", error);
            }
          });

          //проверки кошельков - wallet risk
          stompClient!.subscribe(`/notifications/${userId}/request-wallet-check`, (message) => {
            try {
              const eventData = JSON.parse(message.body) as RequestWalletCheckEventData;

              walletRiskEvents.set(eventData.amlId, eventData);

              notifyWalletRiskSubscribers(eventData.amlId, eventData);
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события проверки риска кошелька:", error);
            }
          });

          //транзакции
          stompClient!.subscribe(`/notifications/${userId}/transaction`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as TransactionEventData;
              const pagePath = window.location.pathname.replace("/", "");
              const bidsId = pagePath.split("bids/")[1];
              const isBidInfoPage = bidsId && messageData.link.includes(bidsId);

              if (isBidInfoPage) {
                setTimeout(() => {
                  bidCard.updateCurrentCard();
                }, delayTimer);

                if (isTransactionListOpen) {
                  bidTransactions.fetch();
                }
              }

              const fetchBalance = getGlobalFetchBalance();
              if (fetchBalance) {
                fetchBalance();
              }

              if (messageData.show) {
                openNotification({
                  message: (
                    <div>
                      <span>По заявке № {messageData.requestNumber}</span>
                      <span
                        className={styles.notificationBlockLink}
                        onClick={() => {
                          window.location.href = messageData.link;
                        }}
                      />
                    </div>
                  ),
                  description:
                    messageData.type === "IN"
                      ? `Зачислен перевод на ${messageData.amount} ${messageData.currency}`
                      : `Перевод на ${messageData.amount} ${messageData.currency} исполнен`,
                  type: NOTIFICATION_TYPE_ENUM.NOTICE,
                });
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о входящих/исходящих транзакциях:", error);
            }
          });

          //транзакции в статусе funding
          stompClient!.subscribe(`/notifications/${userId}/request-processing`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as RequestProcessingEventData;
              const pagePath = window.location.pathname.replace("/", "");

              const bidsId = pagePath.split("bids/")[1];
              const isBidInfoPage = bidsId && messageData.link.includes(bidsId);

              if (isBidInfoPage) {
                setTimeout(() => {
                  bidCard.updateCurrentCard();
                }, delayTimer);
              }

              if (messageData.show) {
                openNotification({
                  message: (
                    <div>
                      <span>Заявка № {messageData.requestNumber}</span>
                      <span
                        className={styles.notificationBlockLink}
                        onClick={() => {
                          window.location.href = messageData.link;
                        }}
                      />
                    </div>
                  ),
                  description: getProcessingMessageText(messageData.processingType),
                  type: NOTIFICATION_TYPE_ENUM.NOTICE,
                });
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о процессе сделки:", error);
            }
          });

          //флаги aml проверок
          stompClient!.subscribe(`/notifications/${userId}/aml-check-status`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as AmlStatusEventData;

              amlCheckStatusEvents.set(messageData.id, messageData);
              notifyAmlCheckStatusSubscribers(messageData.id, messageData);

              const pagePath = window.location.pathname.replace("/", "");
              const paramId = pagePath.split("/").pop();

              const bidsId = pagePath.split("bids/")[1];
              const isBidInfoPage = bidsId && messageData.link.includes(bidsId);

              if (isBidInfoPage && paramId && isRiskManager) {
                bidCard.updateCardById(paramId);
                bidCard.fetchAmlCheckStatus(paramId);
              }

              if (messageData.show && isRiskManager) {
                openNotification({
                  message: (
                    <div>
                      <span>
                        {!messageData.approvedNeededCheck ? "Кошельки проверены" : "Требуется действие по заявке"}
                      </span>
                      <span
                        className={styles.notificationBlockLink}
                        onClick={() => {
                          window.location.href = messageData.link;
                        }}
                      />
                    </div>
                  ),
                  description: !messageData.approvedNeededCheck
                    ? `Кошельки в заявке №${messageData.requestNumber} прошли проверку`
                    : `Проверки в заявке №${messageData.requestNumber} ожидают действий риск офицера`,
                  type: NOTIFICATION_TYPE_ENUM.NOTICE,
                });
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о статусе aml проверки:", error);
            }
          });

          //Обновление грида рублевых заявок на н/с
          stompClient!.subscribe(`/notifications/${userId}/rub-withdrawal-request`, (message) => {
            try {
              rubWithdrawalRequests.fetch();
            } catch (error) {
              console.error(
                "[WEBSOCKET] Ошибка парсинга события о изменениях в гриде заявок номинального аккаунта:",
                error
              );
            }
          });

          //Зачисление на н/с
          stompClient!.subscribe(`/notifications/${userId}/nominal-account-deposit`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as NominalAccountDepositEventData;

              if (messageData.show) {
                openNotification({
                  message: (
                    <Flex vertical>
                      <Typography.Text style={{ color: "#ffffff", fontSize: "16px" }}>
                        На номинальный счёт зачислено
                      </Typography.Text>
                      <Typography.Text style={{ color: "#ffffff", fontSize: "16px" }}>
                        {formatWithDecimals(messageData.amount)} руб.
                      </Typography.Text>
                    </Flex>
                  ),

                  type: NOTIFICATION_TYPE_ENUM.NOTICE,
                });

                nominalAccounts.fetchAllBalances({ rubOperationsFetch: false, rubWithdrawalRequestsFetch: false });
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о зачислении средств на депозит н/с:", error);
            }
          });

          //Обновление н/с при действии других ролей
          stompClient!.subscribe(`/notifications/${userId}/nominal-account`, (message) => {
            const messageData = JSON.parse(message.body) as NominalAccountUpdatedWebSocketEventData;

            try {
              if (!DIRECT_PAYMENT_CUSTOMER_LP) {
                nominalAccountUpdatedEvents.set(messageData.balanceType, messageData);
                notifyNominalAccountUpdatedSubscribers(messageData.balanceType, messageData);

                if (messageData.balanceType === BALANCE_TYPE_ENUM.OVERALL_BALANCES && isOperator) {
                  nominalAccounts.overallBalances.data = {
                    companyId: "",
                    totalBalance: +messageData.totalBalance,
                    availableBalance: +messageData.availableBalance,
                    blockedOnRequests: +messageData.blockedOnRequests,
                    blockedForWithdrawal: +messageData.blockedForWithdrawal,
                  };
                }

                if (messageData.balanceType === BALANCE_TYPE_ENUM.USER_BALANCES) {
                  nominalAccounts.userBalances.data = {
                    companyId: "",
                    totalBalance: +messageData.totalBalance,
                    availableBalance: +messageData.availableBalance,
                    blockedOnRequests: +messageData.blockedOnRequests,
                    blockedForWithdrawal: +messageData.blockedForWithdrawal,
                  };
                }
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события обновления н/с:", error);
            }
          });

          //Продление котировки
          stompClient!.subscribe(`/notifications/${userId}/quote-updated`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as QuoteExtendedEventData;
              const pagePath = window.location.pathname.replace("/", "");

              const bidsId = pagePath.split("bids/")[1];
              const isBidInfoPage = bidsId && messageData.link.includes(bidsId);

              if (isBidInfoPage) {
                setTimeout(() => {
                  quotes.fetch();
                }, delayTimer);
              }

              if (messageData.show) {
                openNotification({
                  message: (
                    <div>
                      <span>Заявка № {messageData.requestNumber}</span>
                      <span
                        className={styles.notificationBlockLink}
                        onClick={() => {
                          window.location.href = messageData.link;
                        }}
                      />
                    </div>
                  ),
                  description: `По заявке ${messageData.requestNumber} продлен срок экспирации`,
                  type: NOTIFICATION_TYPE_ENUM.NOTICE,
                });
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о зачислении средств на депозит н/с:", error);
            }
          });

          //Двойной контроль
          stompClient!.subscribe(`/notifications/${userId}/double-control`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as DoubleControlEventData;
              const pagePath = window.location.pathname.replace("/", "");

              const bidsId = pagePath.split("bids/")[1];
              const isBidInfoPage = bidsId && messageData.link.includes(bidsId);

              if (pagePath === PAGE_PATH_ENUM.PENDING_ACTIONS) {
                pendingActionsBidList.fetch();
              }

              if (messageData.show) {
                openNotification({
                  message: (
                    <div>
                      <span>Заявка № {messageData.requestNumber}</span>
                      <span
                        className={styles.notificationBlockLink}
                        onClick={() => {
                          window.location.href = messageData.link;
                        }}
                      />
                    </div>
                  ),
                  description:
                    messageData.type === OPERATION_TYPE.VERIFY_WALLET_QUOTE
                      ? `Требуется подтверждение адреса кошелька для котировки в заявке ${messageData.requestNumber}`
                      : `Требуется подтверждение адреса кошелька для заявки ${messageData.requestNumber}`,
                  type: NOTIFICATION_TYPE_ENUM.NOTICE,
                });

                pendingStatistics.fetchIndicators();
              }

              if (
                !messageData.show &&
                messageData.type === OPERATION_TYPE.VERIFY_WALLET_REQUEST &&
                (isOperator || isCustomer)
              ) {
                if (isBidInfoPage) {
                  bidCard.updateCurrentCard();
                }
                if (pagePath === PAGE_PATH_ENUM.BIDS) {
                  debouncedBidsFetch();
                }
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о подтверждении кошелька:", error);
            }
          });

          //Балансы
          stompClient!.subscribe(`/notifications/${userId}/balance`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as BalanceEventData;

              const pagePath = window.location.pathname.replace("/", "");

              const bidsId = pagePath.split("bids/")[1];
              const isBidInfoPage = bidsId && messageData.link.includes(bidsId);

              balanceEvents.set(messageData.balanceType, messageData);
              notifyBalanceSubscribers(messageData.balanceType, messageData);

              if (isBidInfoPage) {
                bidCard.updateCurrentCard();
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о балансах:", error);
            }
          });

          //Обновление банковских реквизитов
          //Бек пока выпилил это ивент, но есть вероятность что вернет
          // stompClient!.subscribe(`/notifications/${userId}/bank-statement-updated`, (message) => {
          //   try {
          //     const messageData = JSON.parse(message.body) as BankStatementUpdatedEventData;
          //     const pagePath = window.location.pathname.replace("/", "");
          //     const isBidInfoPage = bidsId && messageData.link.includes(bidsId);

          //     if (isBidInfoPage) {
          //       setTimeout(() => {
          //         bankStatements.setId(messageData.requestNumber);
          //         bankStatements.fetch();
          //       }, delayTimer);
          //     }
          //   } catch (error) {
          //     console.error("[WEBSOCKET] Ошибка парсинга события о обновлении банковских реквизитов:", error);
          //   }
          // });

          //Обновление документов
          stompClient!.subscribe(`/notifications/${userId}/document-updated`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as DocumentUpdatedEventData;
              const pagePath = window.location.pathname.replace("/", "");

              const bidsId = pagePath.split("bids/")[1];
              const isBidInfoPage = bidsId && messageData.link.includes(bidsId);

              if (isBidInfoPage) {
                setTimeout(() => {
                  bidCard.updateCurrentCard();
                }, delayTimer);
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о обновление документов", error);
            }
          });

          //Обновление грида движение по счету на н/с
          stompClient!.subscribe(`/notifications/${userId}/rub-operation`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as RubOperationWebSocketEventData;
              const pagePath = window.location.pathname.replace("/", "");

              if (pagePath === routes.nominalAccount) {
                rubOperations.fetch();
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о рублевых операциях:", error);
            }
          });

          //Обновление комиссий
          stompClient!.subscribe(`/notifications/${userId}/rub-operations-fee-updated`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as RubOperationsFeeUpdatedEvent;

              nominalAccounts.feeStatistics.data = {
                totalFee: messageData.totalFee,
                monthFee: messageData.monthFee,
                dayFee: messageData.dayFee,
              };
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о обновлении комиссий:", error);
            }
          });

          //Завершение сделки
          stompClient!.subscribe(`/notifications/${userId}/safe-deal-rub-withdrawal-request`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as rubWithdrawalRequestSafeDealSucceedEvent;
              const pagePath = window.location.pathname.replace("/", "");
              if (messageData.show) {
                openNotification({
                  message: (
                    <Typography.Text style={{ color: "#ffffff" }}>
                      {safeDealStateTexts[messageData.state]}
                    </Typography.Text>
                  ),
                  type: NOTIFICATION_TYPE_ENUM.NOTICE,
                });
              }

              if (pagePath === routes.nominalAccount) {
                nominalAccounts.fetchAllBalances({
                  rubOperationsFetch: false,
                  rubWithdrawalRequestsFetch: true,
                  userBalancesFetch: true,
                });
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о завершении сделки:", error);
            }
          });

          //Заведение нового договора
          stompClient!.subscribe(`/notifications/${userId}/contract`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as ContractWebSocketEventData;

              if (messageData.show) {
                openNotification({
                  message: (
                    <div>
                      <span>Создан договор {messageData.unk}</span>
                      <span
                        className={styles.notificationBlockLink}
                        onClick={() => {
                          window.location.href = messageData.link;
                        }}
                      />
                    </div>
                  ),
                  type: NOTIFICATION_TYPE_ENUM.NOTICE,
                });
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о заведении нового договора:", error);
            }
          });

          //Обновление статуса договора
          stompClient!.subscribe(`/notifications/${userId}/contract-updated`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as ContractStatusUpdatedWebSocketEventData;
              const pagePath = window.location.pathname.replace("/", "");
              const isContractInfoPage = pagePath.includes(messageData.contractHistory.contractId);

              if (messageData.show) {
                openNotification({
                  message: (
                    <Typography.Text style={{ color: "#ffffff" }}>
                      Договор УНК {messageData.unk}
                      <span
                        className={styles.notificationBlockLink}
                        onClick={() => {
                          window.location.href = messageData.contractHistory.contractId;
                        }}
                      />
                    </Typography.Text>
                  ),
                  description: `Перешёл в статус ${CONTRACT_STATUSES.texts[messageData.contractHistory.status]}`,
                  type: NOTIFICATION_TYPE_ENUM.NOTICE,
                });
              }

              if (isContractInfoPage) {
                contractCard.fetchCardById(messageData.contractHistory.contractId);
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о обновлении статуса договора:", error);
            }
          });

          //Отклонение котировки
          stompClient!.subscribe(`/notifications/${userId}/quote-reject`, (message) => {
            try {
              const messageData = JSON.parse(message.body) as QuoteRejectWebSocketEventData;
              if (messageData.show) {
                openNotification({
                  message: (
                    <span>{`Котировка в заявке № ${messageData.requestNumber} на ${formatWithDecimals(messageData.amount)} ${messageData.tokenCurrency} и ${formatWithDecimals(messageData.fiatAmount)} ${messageData.fiatCurrency} не была выбрана`}</span>
                  ),
                  type: NOTIFICATION_TYPE_ENUM.NOTICE,
                });
              }
            } catch (error) {
              console.error("[WEBSOCKET] Ошибка парсинга события о отклонении котировки:", error);
            }
          });
        },

        onStompError: (frame) => {
          console.error("[STOMP] Ошибка STOMP:", frame.headers["message"]);
          globalWebSocketState.isConnected = false;
          globalWebSocketState.client = null;
          updateSubscribers();
        },
        onWebSocketError: (error) => {
          console.error("[STOMP] Ошибка WebSocket:", error);
          globalWebSocketState.isConnected = false;
          globalWebSocketState.client = null;
          updateSubscribers();
        },
        onWebSocketClose: () => {
          globalWebSocketState.isConnected = false;
          globalWebSocketState.client = null;
          updateSubscribers();
        },
      });

      stompClient.activate();
      stompClientRef.current = stompClient;
    }

    return () => {
      if (stompClient) {
        globalWebSocketState.isConnected = false;
        globalWebSocketState.client = null;
        updateSubscribers();
        stompClient.deactivate();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const subscribe = useCallback((destination: string, callback: (message: any) => void): StompSubscription | null => {
    if (globalWebSocketState.isConnected && globalWebSocketState.client) {
      return globalWebSocketState.client.subscribe(destination, callback);
    }
    return null;
  }, []);

  const getConnectionState = useCallback(() => {
    return {
      isConnected: globalWebSocketState.isConnected,
      client: globalWebSocketState.client,
    };
  }, []);

  return {
    isConnected: globalWebSocketState.isConnected,
    subscribe,
    getConnectionState,
  };
};

export const useWalletCheckEvents = () => {
  const subscribeToWalletCheck = useCallback((amlId: string, callback: (event: UserWalletCheckEventData) => void) => {
    const handler = (eventAmlId: string, event: UserWalletCheckEventData) => {
      if (eventAmlId === amlId) {
        callback(event);
      }
    };

    walletCheckSubscribers.add(handler);

    const existingEvent = walletCheckEvents.get(amlId);
    if (existingEvent) {
      callback(existingEvent);
    }

    return () => {
      walletCheckSubscribers.delete(handler);
    };
  }, []);

  const getWalletCheckEvent = useCallback((amlId: string): UserWalletCheckEventData | undefined => {
    return walletCheckEvents.get(amlId);
  }, []);

  return {
    subscribeToWalletCheck,
    getWalletCheckEvent,
    isConnected: globalWebSocketState.isConnected,
  };
};

export const useWalletRiskEvents = () => {
  const subscribeToWalletRisk = useCallback((amlId: string, callback: (event: RequestWalletCheckEventData) => void) => {
    const handler = (eventAmlId: string, event: RequestWalletCheckEventData) => {
      if (eventAmlId === amlId) {
        callback(event);
      }
    };

    walletRiskSubscribers.add(handler);

    const existingEvent = walletRiskEvents.get(amlId);
    if (existingEvent) {
      callback(existingEvent);
    }

    return () => {
      walletRiskSubscribers.delete(handler);
    };
  }, []);

  const getWalletRiskEvent = useCallback((checkId: string): RequestWalletCheckEventData | undefined => {
    return walletRiskEvents.get(checkId);
  }, []);

  return {
    subscribeToWalletRisk,
    getWalletRiskEvent,
    isConnected: globalWebSocketState.isConnected,
  };
};

export const useAmlCheckStatusEvents = () => {
  const subscribeToAmlCheckStatus = useCallback((bidId: string, callback: (event: AmlStatusEventData) => void) => {
    const handler = (eventBidId: string, event: AmlStatusEventData) => {
      if (eventBidId === bidId) {
        callback(event);
      }
    };

    amlCheckStatusSubscribers.add(handler);

    const existingEvent = amlCheckStatusEvents.get(bidId);
    if (existingEvent) {
      callback(existingEvent);
    }

    return () => {
      amlCheckStatusSubscribers.delete(handler);
    };
  }, []);

  const getAmlCheckStatusEvent = useCallback((bidId: string): AmlStatusEventData | undefined => {
    return amlCheckStatusEvents.get(bidId);
  }, []);

  return {
    subscribeToAmlCheckStatus,
    getAmlCheckStatusEvent,
    isConnected: globalWebSocketState.isConnected,
  };
};

export const useBalanceEvents = () => {
  const subscribeToBalance = useCallback((type: BALANCE_ENUM, callback: (event: BalanceEventData) => void) => {
    const handler = (eventType: BALANCE_ENUM, event: BalanceEventData) => {
      if (eventType === type) {
        callback(event);
      }
    };

    balanceSubscribers.add(handler);

    const existingEvent = balanceEvents.get(type);
    if (existingEvent) {
      callback(existingEvent);
    }

    return () => {
      balanceSubscribers.delete(handler);
    };
  }, []);

  const getBalanceEvent = useCallback((type: BALANCE_ENUM): BalanceEventData | undefined => {
    return balanceEvents.get(type);
  }, []);

  return {
    subscribeToBalance,
    getBalanceEvent,
    isConnected: globalWebSocketState.isConnected,
  };
};

export const useNominalAccountUpdatedEvents = () => {
  const subscribeToNominalAccountUpdated = useCallback(
    (type: BALANCE_TYPE_ENUM, callback: (event: NominalAccountUpdatedWebSocketEventData) => void) => {
      const handler = (eventType: BALANCE_TYPE_ENUM, event: NominalAccountUpdatedWebSocketEventData) => {
        if (eventType === type) {
          callback(event);
        }
      };
    },
    []
  );

  const getNominalAccountUpdatedEvent = useCallback(
    (type: BALANCE_TYPE_ENUM): NominalAccountUpdatedWebSocketEventData | undefined => {
      return nominalAccountUpdatedEvents.get(type);
    },
    []
  );

  return {
    subscribeToNominalAccountUpdated,
    getNominalAccountUpdatedEvent,
    isConnected: globalWebSocketState.isConnected,
  };
};

const getProcessingMessageText = (type: PROCESSING_TYPE) => {
  switch (type) {
    case "FIAT_SENT":
      return "Рубли отправлены";
    case "FIAT_RECEIVED":
      return "Рубли получены";
    case "TOKEN_RECEIVED":
      return "Обеспечена криптовалютой";
    case "AWAIT_CUSTOMER_EXECUTED":
      return "подтвердите исполнение заявки";
    case "CUSTOMER_EXECUTED":
      return "готова к исполнению";
  }
};
